import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import { Close, CheckCircle } from "@mui/icons-material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { ExamTypes } from "../../Utils/Constants";
import { AddExamFormData } from "../../Utils/Types";
import GenericModal from "../GenericModal";
import { EWidths } from "../../Utils/Enums";
import styles from "../../Styles/addExamModal.module.css";

interface AddExamModalProps {
  open: boolean;
  formData: AddExamFormData;
  onClose: () => void;
  onChange: (field: keyof AddExamFormData, value: string) => void;
  onSubmit: () => void;
  activeOrders: any[];
}

export default function AddExamModal({
  open,
  formData,
  onClose,
  onChange,
  onSubmit,
  activeOrders,
}: AddExamModalProps) {
  // Filter out certificate products from display
  const nonCertificateOrders = activeOrders.filter(
    (order) => !order.product?.isCertificate
  );

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      title={Vocabulary.addNewExam}
      maxWidth={EWidths.LG}
      width="800px"
      actions={
        <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
          <Button
            variant="outlined"
            onClick={onClose}
            startIcon={<Close />}
            fullWidth
            color="error"
            sx={{ borderRadius: "8px" }}
          >
            {Vocabulary.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            startIcon={<CheckCircle />}
            fullWidth
            sx={{
              borderRadius: "8px",
              boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}50`,
              "&:hover": {
                boxShadow: (theme) => `0 6px 20px ${theme.palette.primary.main}66`,
              },
            }}
          >
            {Vocabulary.add}
          </Button>
        </Stack>
      }
    >
      <Box className={styles.modalContent}>
        <Box className={styles.contentWrapper}>
          {/* Left Side - Icon */}
          <Box className={styles.iconSection}>
            <Box
              component="img"
              src="/exam_icon.png"
              alt="Exam scheduling"
              className={styles.sideIcon}
            />
          </Box>

          {/* Right Side - Form Content */}
          <Box className={styles.formContent}>
            {/* Service Selection - Visible only when multiple orders */}
            {nonCertificateOrders.length > 1 && (
              <Box className={styles.sectionContainer}>
                <Typography className={styles.sectionTitle}>
                  {Vocabulary.selectService}
                </Typography>
                <Stack direction="row" spacing={1} className={styles.chipContainer}>
                  {nonCertificateOrders.map((order, index) => (
                    <Chip
                      key={order._id}
                      label={order.product?.name || `Service ${index + 1}`}
                      onClick={() => onChange("orderId", order._id)}
                      color={formData.orderId === order._id ? "primary" : "default"}
                      variant={formData.orderId === order._id ? "filled" : "outlined"}
                      className={styles.serviceChip}
                      sx={{
                        fontSize: "0.9rem",
                        height: "36px",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: (theme) =>
                            `0 4px 12px ${theme.palette.primary.main}30`,
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Exam Details Section */}
            <Box className={styles.sectionContainer}>
              <Typography className={styles.sectionTitle}>
                {Vocabulary.selectExamTypeAndDate}
              </Typography>
              <Stack spacing={2}>
                {/* Exam Type Selection */}
                <FormControl fullWidth>
                  <InputLabel sx={{ fontWeight: 500 }}>
                    {Vocabulary.type}
                  </InputLabel>
                  <Select
                    value={formData.type}
                    label={Vocabulary.type}
                    onChange={(e) => onChange("type", e.target.value)}
                    sx={{
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": {
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: (theme) =>
                            `0 2px 8px ${theme.palette.primary.main}15`,
                        },
                      },
                    }}
                  >
                    <MenuItem value={ExamTypes.THEORETIC}>
                      {ExamTypes.THEORETIC}
                    </MenuItem>
                    <MenuItem value={ExamTypes.PRACTIC}>
                      {ExamTypes.PRACTIC}
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Exam Date Selection */}
                <TextField
                  label={Vocabulary.selectExamDate}
                  type="date"
                  value={formData.examDate}
                  onChange={(e) => onChange("examDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: new Date().toISOString().split("T")[0],
                  }}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: (theme) =>
                          `0 2px 8px ${theme.palette.primary.main}15`,
                      },
                    },
                  }}
                />
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>
    </GenericModal>
  );
}
